import { useCallback, useState } from 'react';
import { MdOutlineModeEditOutline } from 'react-icons/md';
import { ActionIcon, Button, cn, Tooltip } from 'rizzui';
import { toast } from 'sonner';
import DeletePopover from '../../../components/buttons/DeletePopover';
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import CustomTable from '../../../components/table/CustomTable';
import useFetch from '../../../hooks/useFetch';
import useFilters from '../../../hooks/useFilters';
import useHandleErrors from '../../../hooks/useHandleErrors';
import { ProductTypeService } from '../../../services/product-type/productTypeService';
import { ProductTypesApiResponse } from '../../../type/entities/product-type';
import ProductTypeModalForm, { renewalSettingsOptions } from './ProductTypeModalForm';

const columnOrder = [
    'id',
    'name',
    'description',
    'renewalSettings',
    'renewalDaysNumber',
    'consultancyFieldsRequired',
    'contractTemplate',
    'canBeDeleted',
    'templateConceptClientInvoice',
    'templateConceptTutorInvoice',
    'templateConceptInternalInvoice',
];

const ProductTypesList = () => {
    const emptySelectedProductType = {
        name: "",
        description: "",
        renewalSettings: null,
        renewalDaysNumber: null,
        consultancyFieldsRequired: false,
        contractTemplate: null,
        contractWatermarkDocument: null,
        templateConceptClientInvoice: null,
        templateConceptTutorInvoice: null,
        templateConceptInternalInvoice: null,
    };

    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize } = useFilters();
    const { handleErrors } = useHandleErrors();
    const { userCan } = usePrivilege();
    const productTypeService = new ProductTypeService();

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedProductType, setSelectedProductType] = useState<any>(emptySelectedProductType);

    const [productTypes, loading, error, refetch] = useFetch(useCallback(async () => {
        const response = await productTypeService.listProductTypes(filters);
        return response.getResponseData() as ProductTypesApiResponse;
    }, [filters]));

    const handleDelete = async (id: string) => {
        const response = await (await productTypeService.deleteProductType(id)).getResponseData();
        if (response.success) {
            toast.success('Tipo de producto eliminado correctamente');
            refetch();
        } else {
            handleErrors(response);
        }
    };

    const handleMultiDelete = async (ids: string[]) => {
        const response = await (await productTypeService.deleteMultipleProductTypes(ids)).getResponseData();
        if (response.success) {
            toast.success('Tipos de productos eliminados correctamente');
            refetch();
        } else {
            handleErrors(response);
        }
    };

    const handleEditProductType = useCallback(async (productType: any) => {
        let productTypeData = productType;

        try {
            const response = await productTypeService.getProductTypeById(productType.id);
            const responseData = response.getResponseData();
            if (responseData?.success && responseData.data) {
                productTypeData = responseData.data;
            }
        } catch (error) {
            toast.error('No se ha podido cargar la configuración completa del tipo de producto');
        }

        setSelectedProductType({
            productTypeId: productTypeData.id,
            name: productTypeData.name,
            description: productTypeData.description,
            renewalSettings: productTypeData.renewalSettings,
            renewalDaysNumber: productTypeData.renewalDaysNumber,
            consultancyFieldsRequired: productTypeData.consultancyFieldsRequired ?? false,
            contractTemplate: productTypeData.contractTemplate ?? null,
            contractWatermarkDocument: productTypeData.contractWatermarkDocument ?? null,
            templateConceptClientInvoice: productTypeData.templateConceptClientInvoice ?? null,
            templateConceptTutorInvoice: productTypeData.templateConceptTutorInvoice ?? null,
            templateConceptInternalInvoice: productTypeData.templateConceptInternalInvoice ?? null,
        });
        setIsModalOpen(true);
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        setSelectedProductType(emptySelectedProductType);
    }, []);

    const getRenewalSettingsLabel = (settings: string | null) => {
        if (!settings) return 'Sin configuración';
        return renewalSettingsOptions.find(option => option.value === settings)?.label || settings;
    };

    const parseBooleanValue = (value: any): boolean => [true, 1, '1', 'true', 't'].includes(value);

    /**
     * Order the data to match the column order
     */
    const orderedData = () => productTypes?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });

    return (
        <div className="space-y-6">
            {productTypes !== undefined && (
                <CustomTable
                    largeTable
                    id={'product-types-table'}
                    columnOrder={columnOrder}
                    data={orderedData()}
                    isLoading={loading}
                    columnsNotShown={['canBeDeleted', 'contractTemplate', 'templateConceptClientInvoice', 'templateConceptTutorInvoice', 'templateConceptInternalInvoice']}
                    overrideColumns={[
                        {
                            key: 'renewalSettings',
                            render: (row: any) => {
                                return (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                        {getRenewalSettingsLabel(row.row.original.renewalSettings)}
                                    </span>
                                );
                            },
                        },
                        {
                            key: 'renewalDaysNumber',
                            render: (row: any) => {
                                return (
                                    <div className="text-sm text-gray-600">
                                        {row.row.original.renewalDaysNumber ? (
                                            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-green-100 text-green-800">
                                                {row.row.original.renewalDaysNumber} días
                                            </span>
                                        ) : (
                                            <span className="text-gray-400">-</span>
                                        )}
                                    </div>
                                );
                            },
                        },
                        {
                            key: 'consultancyFieldsRequired',
                            render: (row: any) => {
                                const isRequired = parseBooleanValue(row.row.original.consultancyFieldsRequired);

                                return (
                                    <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${isRequired ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>
                                        {isRequired ? 'Sí' : 'No'}
                                    </span>
                                );
                            },
                        },
                    ]}
                    actions={[
                        {
                            label: 'Edit',
                            permissions: { group: 'product_types', permission: 'edit_product_types' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Editar tipo de producto'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 cursor-pointer"
                                            onClick={() => handleEditProductType(row)}
                                        >
                                            <MdOutlineModeEditOutline className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Delete',
                            permissions: { group: 'product_types', permission: 'delete_product_types' },
                            render: (row: any) => {
                                if (!row.canBeDeleted) return null;
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Eliminar'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <div>
                                            <DeletePopover
                                                title={`Eliminar tipo de producto`}
                                                description={`¿Estás seguro de que deseas eliminar el tipo de producto "${row.name}"?`}
                                                onDelete={() => handleDelete(row.id)}
                                            />
                                        </div>
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                    enableRowSelection="canBeDeleted"
                    handleMultipleDelete={handleMultiDelete}
                    multiDeletePermission={{ group: 'product_types', action: 'delete_product_types' }}
                    filter={false}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: productTypes ? productTypes.lastPage : 1,
                        totalCount: productTypes?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                >
                    { userCan('create_product_types', 'product_types') && (
                        <Button
                            onClick={() => setIsModalOpen(true)}
                        >
                            Crear tipo de producto
                        </Button>
                    )}
                </CustomTable>
            )}

            <ProductTypeModalForm
                data={selectedProductType}
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                refetch={refetch}
            />
        </div>
    );
};

export default ProductTypesList;
